import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  LinearProgress,
  Alert,
  Card,
  CardMedia,
  CardContent,
  CardActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SeoHead from '../components/common/SeoHead';
import { useAuth } from '../contexts/AuthContext';
import ProductCard from '../components/products/ProductCard';
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  limit, 
  orderBy,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { app as firebaseApp, functions, functionsWithLongTimeout } from '../firebase/firebase';

// 样式组件
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const ElementBar = styled(Box)(({ theme }) => ({
  height: '24px',
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '24px',
  marginLeft: '10px',
}));

const BaziColumn = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f8f8',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  textAlign: 'center',
}));

const BaziChar = styled(Box)(({ theme }) => ({
  fontSize: '24px',
  lineHeight: 1.5,
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
}));

// 常量定义
const heavenlyStem = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
const earthlyBranch = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];

// 二十四节气表（2000年为基准年）
const solarTerms = [
  { name: '立春', month: 2, day: 4 },   // 寅月开始
  { name: '惊蛰', month: 3, day: 6 },   // 卯月开始
  { name: '清明', month: 4, day: 5 },   // 辰月开始
  { name: '立夏', month: 5, day: 6 },   // 巳月开始
  { name: '芒种', month: 6, day: 6 },   // 午月开始
  { name: '小暑', month: 7, day: 7 },   // 未月开始
  { name: '立秋', month: 8, day: 8 },   // 申月开始
  { name: '白露', month: 9, day: 8 },   // 酉月开始
  { name: '寒露', month: 10, day: 8 },  // 戌月开始
  { name: '立冬', month: 11, day: 7 },  // 亥月开始
  { name: '大雪', month: 12, day: 7 },  // 子月开始
  { name: '小寒', month: 1, day: 6 }    // 丑月开始
];

// 时辰列表
const shichenList = [
  { label: '子时 (23:00 - 00:59)', value: '子时' },
  { label: '丑时 (01:00 - 02:59)', value: '丑时' },
  { label: '寅时 (03:00 - 04:59)', value: '寅时' },
  { label: '卯时 (05:00 - 06:59)', value: '卯时' },
  { label: '辰时 (07:00 - 08:59)', value: '辰时' },
  { label: '巳时 (09:00 - 10:59)', value: '巳时' },
  { label: '午时 (11:00 - 12:59)', value: '午时' },
  { label: '未时 (13:00 - 14:59)', value: '未时' },
  { label: '申时 (15:00 - 16:59)', value: '申时' },
  { label: '酉时 (17:00 - 18:59)', value: '酉时' },
  { label: '戌时 (19:00 - 20:59)', value: '戌时' },
  { label: '亥时 (21:00 - 22:59)', value: '亥时' }
];

const shichenToHourMap = {
  '子时': '23:00', '丑时': '01:00', '寅时': '03:00', '卯时': '05:00',
  '辰时': '07:00', '巳时': '09:00', '午时': '11:00', '未时': '13:00',
  '申时': '15:00', '酉时': '17:00', '戌时': '19:00', '亥时': '21:00'
};

// 五行关系
const FiveElements = {
  generates: {
    '木': '火', '火': '土', '土': '金', '金': '水', '水': '木'
  },
  restricts: {
    '木': '金', '火': '水', '土': '木', '金': '火', '水': '土'
  }
};

// Firebase 集成
const db = getFirestore(firebaseApp);
// 使用从firebase.js导入的functions和functionsBaseUrl
// 这样确保全局一致的Firebase Functions配置

const AiFortune = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  
  // 添加SEO结构化数据
  const aiFortuneSchemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI智能算命 | 生辰八字分析 | 免费运势预测",
    "applicationCategory": "LifestyleApplication",
    "description": "基于先进AI技术的智能算命系统，只需输入生辰八字，即可获得精准的运势分析和人生指引，解答您的情感、事业、财运等方面困惑。",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "SGD"
    },
    "operatingSystem": "All",
    "url": "https://destiny-crystal.com/ai-fortune"
  };

  // 表单数据
  const [form, setForm] = useState({
    birthDate: '',
    birthShichen: '',
    name: '',
    gender: 'male' // 默认值为男性
  });

  // 处理表单变更
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prevForm => ({
      ...prevForm,
      [name]: value
    }));
  };
  
  // 五行提取函数 - 从算命结果中提取五行属性
  const extractElementsAndRecommendBracelets = (fortuneText) => {
    console.log('开始五行元素提取和分析...');
    // 初始化五行计数
    const elements = {
      metal: 0, // 金
      wood: 0,  // 木
      water: 0, // 水
      fire: 0,  // 火
      earth: 0  // 土
    };
    
    // 中文五行关键词映射
    const chineseElements = {
      '金': 'metal',
      '木': 'wood',
      '水': 'water',
      '火': 'fire',
      '土': 'earth'
    };

    // 解析文本中的五行关键词
    const fortuneTextLower = fortuneText.toLowerCase();
    
    // 检查明确的五行命运关键词
    if (fortuneText.includes('金命') || fortuneText.includes('金相') || fortuneText.includes('金行')) {
      elements.metal += 3;
      console.log('检测到关键词: 金命/金相/金行');
    }
    if (fortuneText.includes('木命') || fortuneText.includes('木相') || fortuneText.includes('木行')) {
      elements.wood += 3;
      console.log('检测到关键词: 木命/木相/木行');
    }
    if (fortuneText.includes('水命') || fortuneText.includes('水相') || fortuneText.includes('水行')) {
      elements.water += 3;
      console.log('检测到关键词: 水命/水相/水行');
    }
    if (fortuneText.includes('火命') || fortuneText.includes('火相') || fortuneText.includes('火行')) {
      elements.fire += 3;
      console.log('检测到关键词: 火命/火相/火行');
    }
    if (fortuneText.includes('土命') || fortuneText.includes('土相') || fortuneText.includes('土行')) {
      elements.earth += 3;
      console.log('检测到关键词: 土命/土相/土行');
    }
    
    // 检查五行偏弱/偏强的描述
    if (fortuneText.includes('金行偏弱') || fortuneText.includes('金行不足')) {
      elements.metal -= 2;
      console.log('检测到金行偏弱');
    }
    if (fortuneText.includes('木行偏弱') || fortuneText.includes('木行不足')) {
      elements.wood -= 2;
      console.log('检测到木行偏弱');
    }
    if (fortuneText.includes('水行偏弱') || fortuneText.includes('水行不足')) {
      elements.water -= 2;
      console.log('检测到水行偏弱');
    }
    if (fortuneText.includes('火行偏弱') || fortuneText.includes('火行不足')) {
      elements.fire -= 2;
      console.log('检测到火行偏弱');
    }
    if (fortuneText.includes('土行偏弱') || fortuneText.includes('土行不足')) {
      elements.earth -= 2;
      console.log('检测到土行偏弱');
    }
    
    if (fortuneText.includes('金行偏强') || fortuneText.includes('金行过旺')) {
      elements.metal += 2;
      console.log('检测到金行偏强');
    }
    if (fortuneText.includes('木行偏强') || fortuneText.includes('木行过旺')) {
      elements.wood += 2;
      console.log('检测到木行偏强');
    }
    if (fortuneText.includes('水行偏强') || fortuneText.includes('水行过旺')) {
      elements.water += 2;
      console.log('检测到水行偏强');
    }
    if (fortuneText.includes('火行偏强') || fortuneText.includes('火行过旺')) {
      elements.fire += 2;
      console.log('检测到火行偏强');
    }
    if (fortuneText.includes('土行偏强') || fortuneText.includes('土行过旺')) {
      elements.earth += 2;
      console.log('检测到土行偏强');
    }
    
    // 检查水晶建议
    if (fortuneText.includes('佩戴金行') || fortuneText.includes('佩戴金属类')) {
      elements.metal += 1;
      console.log('检测到建议佩戴金行水晶');
    }
    if (fortuneText.includes('佩戴木行') || fortuneText.includes('佩戴绿色')) {
      elements.wood += 1;
      console.log('检测到建议佩戴木行水晶');
    }
    if (fortuneText.includes('佩戴水行') || fortuneText.includes('佩戴蓝色')) {
      elements.water += 1;
      console.log('检测到建议佩戴水行水晶');
    }
    if (fortuneText.includes('佩戴火行') || fortuneText.includes('佩戴红色')) {
      elements.fire += 1;
      console.log('检测到建议佩戴火行水晶');
    }
    if (fortuneText.includes('佩戴土行') || fortuneText.includes('佩戴黄色')) {
      elements.earth += 1;
      console.log('检测到建议佩戴土行水晶');
    }
    
    // 从文本中直接提取推荐元素（如果存在）
    const recommendPattern = /建议佩戴([金木水火土])行/;
    const recommendMatch = fortuneText.match(recommendPattern);
    
    if (recommendMatch && recommendMatch[1]) {
      const recommendedElement = chineseElements[recommendMatch[1]];
      if (recommendedElement) {
        elements[recommendedElement] += 5; // 给直接建议的元素高权重
        console.log(`明确建议佩戴: ${recommendMatch[1]}行 (${recommendedElement})`);
      }
    }
    
    console.log('五行元素分析计数结果:', elements);
    
    // 确定主要五行属性
    const dominantElement = Object.entries(elements).reduce(
      (max, [element, count]) => count > max[1] ? [element, count] : max,
      ['none', -999]
    )[0];
    
    console.log('确定的主要五行属性:', dominantElement);
    
    // 如果有明确的元素，获取相应的手串推荐
    if (dominantElement !== 'none') {
      // 将英文元素转换为中文元素名
      const chineseElementMap = {
        'metal': '金',
        'wood': '木',
        'water': '水',
        'fire': '火',
        'earth': '土'
      };
      
      const chineseElement = chineseElementMap[dominantElement];
      fetchRecommendedBracelets(chineseElement);
    } else {
      console.log('无法确定主要五行属性，使用默认推荐');
      // 使用默认的通用推荐
      fetchRecommendedBracelets('');
    }
  };
  
  // 根据五行属性获取推荐手串
  const fetchRecommendedBracelets = async (element) => {
    console.log(`开始获取${element ? element + '行' : '通用'}推荐手串...`);
    setLoadingProducts(true);
    try {
      let bracelets = [];
      
      if (element) {
        // 修改查询方式，避免需要复杂索引
        // 只使用一个复合条件，其余在客户端过滤
        const q = query(
          collection(db, 'products'),
          where('status', '==', 'active'),
          limit(20)
        );
        
        const querySnapshot = await getDocs(q);
        const allProducts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log(`状态为active的产品总数: ${allProducts.length}`);
        
        // 客户端过滤匹配的产品
        bracelets = allProducts.filter(product => 
          // 检查分类是否匹配
          product.categoryId === element ||
          // 或者检查tags是否包含该元素
          (Array.isArray(product.tags) && product.tags.includes(element)) ||
          // 或者检查elements数组是否包含该元素
          (Array.isArray(product.elements) && product.elements.includes(element))
        );
        
        // 如果没有足够的匹配，使用关键词匹配
        if (bracelets.length < 3) {
          const elementKeywords = {
            '金': ['金', '白水晶', '黄水晶', '白色', '金色'],
            '木': ['木', '绿色', '绿幽灵', '绿东陵', '翡翠'],
            '水': ['水', '蓝色', '黑色', '黑曜石', '蓝宝石'],
            '火': ['火', '红色', '红玛瑙', '石榴石', '红碧玺'],
            '土': ['土', '黄色', '黄玉', '黄水晶', '虎睛石']
          };
          
          const keywords = elementKeywords[element] || [];
          
          // 找出含有关键词的产品
          const keywordMatches = allProducts.filter(product => {
            // 跳过已匹配的产品
            if (bracelets.some(b => b.id === product.id)) return false;
            
            return (
              // 检查产品名称
              keywords.some(keyword => product.name && product.name.includes(keyword)) ||
              // 检查描述
              keywords.some(keyword => product.description && product.description.includes(keyword)) ||
              // 检查标签
              (Array.isArray(product.tags) && product.tags.some(tag => 
                keywords.some(keyword => tag.includes(keyword))
              ))
            );
          });
          
          // 合并结果，最多取3个产品
          bracelets = [...bracelets, ...keywordMatches].slice(0, 3);
        }
      } else {
        // 没有指定元素时，只查询active状态的产品
        const q = query(
          collection(db, 'products'),
          where('status', '==', 'active'),
          limit(3)
        );
        
        const querySnapshot = await getDocs(q);
        bracelets = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }
      
      console.log(`匹配的产品数量: ${bracelets.length}`);
      
      // 如果没有获取到产品，显示空状态而不是使用模拟数据
      if (bracelets.length < 1) {
        console.log('未找到匹配的产品');
        // 不添加模拟数据，保持空数组，由UI处理空状态
      }
      
      console.log('最终推荐产品列表:', bracelets.map(p => p.name));
      setRecommendedProducts(bracelets.slice(0, 3)); // 确保最多3个产品
    } catch (err) {
      console.error('获取推荐手串失败:', err);
      // 出错时不使用模拟数据，而是保持空数组
      setRecommendedProducts([]);
      // 设置错误信息让用户知道发生了问题
      setError('无法获取推荐产品，请稍后再试');
    } finally {
      setLoadingProducts(false);
    }
  };

  // 节气计算函数
  function getSolarTermTime(year, termIndex) {
    const baseYear = 2000;
    const baseTermDays = [6, 21, 4, 19, 6, 21, 5, 20, 6, 21, 6, 22, 7, 23, 8, 23, 8, 23, 8, 24, 8, 22, 7, 22];
    
    const yearDiff = year - baseYear;
    const offset = Math.floor(yearDiff * 0.2422);
    const baseDay = baseTermDays[termIndex];
    const actualDay = baseDay + offset;
    
    return new Date(year, Math.floor(termIndex / 2), actualDay);
  }

  // 八字计算函数
  function calculateBaZi(dateStr, timeStr) {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = parseInt(timeStr.split(':')[0], 10);

    // 计算年柱
    function calculateYearPillar() {
      // 以立春为界
      const lichunDate = getSolarTermTime(year, 2); // 立春是第3个节气
      let offsetYear = year;
      if (date < lichunDate) {
        offsetYear = year - 1;
      }
      
      const cycle = (offsetYear - 4) % 60;
      const yearStem = heavenlyStem[cycle % 10];
      const yearBranch = earthlyBranch[cycle % 12];
      
      return { stem: yearStem, branch: yearBranch };
    }

    // 计算月柱
    function calculateMonthPillar(dateStr, yearStem) {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();

      // 月干偏移表
      const monthGanOffsets = {
        '甲': 2, '己': 2,  // 丙寅
        '乙': 4, '庚': 4,  // 戊寅
        '丙': 6, '辛': 6,  // 庚寅
        '丁': 8, '壬': 8,  // 壬寅
        '戊': 0, '癸': 0   // 甲寅
      };

      // 计算月份索引
      let monthIndex = -1;
      
      // 特殊处理正月前
      if (month === 1 || (month === 2 && day < solarTerms[0].day)) {
        monthIndex = 11;  // 丑月
      } else {
        // 查找当前月所属节气
        for (let i = 0; i < solarTerms.length; i++) {
          const currentTerm = solarTerms[i];
          const nextTerm = solarTerms[(i + 1) % 12];
          
          if (
            (month === currentTerm.month && day >= currentTerm.day) ||
            (month === nextTerm.month && day < nextTerm.day) ||
            (month > currentTerm.month && month < nextTerm.month)
          ) {
            monthIndex = i;
            break;
          }
        }
      }

      // 计算月干
      const baseOffset = monthGanOffsets[yearStem];
      const monthStemIndex = (baseOffset + monthIndex) % 10;
      
      // 计算月支（寅月为正月）
      const monthBranchIndex = monthIndex % 12;

      // 获取最终干支
      const monthStem = heavenlyStem[monthStemIndex];
      const monthBranch = earthlyBranch[(monthBranchIndex + 2) % 12];

      return {
        stem: monthStem,
        branch: monthBranch
      };
    }

    // 计算日柱
    function calculateDayPillar() {
      // 以1900年1月31日甲辰日为基准
      const baseDate = new Date(1900, 0, 31);
      const daysDiff = Math.floor((date - baseDate) / (24 * 60 * 60 * 1000));
      
      // 以甲辰日为起点推算
      const cycle = (daysDiff + 40) % 60;
      const dayStem = heavenlyStem[cycle % 10];
      const dayBranch = earthlyBranch[cycle % 12];
      
      return { stem: dayStem, branch: dayBranch };
    }

    // 计算时柱
    function calculateHourPillar(dayStem) {
      const branchHour = Math.floor((hour + 1) / 2) % 12;
      
      // 根据日干推时干
      const dayIndex = heavenlyStem.indexOf(dayStem);
      const stemBase = (dayIndex % 5) * 2;
      const hourStem = heavenlyStem[(stemBase + Math.floor((hour + 1) / 2)) % 10];
      
      return {
        stem: hourStem,
        branch: earthlyBranch[branchHour]
      };
    }

    // 计算四柱
    const yearPillar = calculateYearPillar();
    const monthPillar = calculateMonthPillar(dateStr, yearPillar.stem);
    const dayPillar = calculateDayPillar();
    const hourPillar = calculateHourPillar(dayPillar.stem);

    return {
      year: yearPillar.stem + yearPillar.branch,
      month: monthPillar.stem + monthPillar.branch,
      day: dayPillar.stem + dayPillar.branch,
      hour: hourPillar.stem + hourPillar.branch
    };
  }

  // 五行分析函数
  function analyzeFiveElements(bazi) {
    const elementMap = {
      '甲': '木', '乙': '木', '丙': '火', '丁': '火', '戊': '土',
      '己': '土', '庚': '金', '辛': '金', '壬': '水', '癸': '水',
      '子': '水', '丑': '土', '寅': '木', '卯': '木', '辰': '土',
      '巳': '火', '午': '火', '未': '土', '申': '金', '酉': '金',
      '戌': '土', '亥': '水'
    };

    const counts = { '木': 0, '火': 0, '土': 0, '金': 0, '水': 0 };

    // 统计天干地支中各五行的数量
    Object.values(bazi).forEach(pillar => {
      counts[elementMap[pillar[0]]]++;  // 天干的五行
      counts[elementMap[pillar[1]]]++;  // 地支的五行
    });

    // 找出最缺乏的五行
    let minElement = '木';
    let minCount = counts['木'];
    
    for (const [element, count] of Object.entries(counts)) {
      if (count < minCount) {
        minElement = element;
        minCount = count;
      }
    }

    // 确定补充方式（相生或相克）
    const recommendation = {
      element: minElement,
      method: 'generates',
      recommended: FiveElements.generates[minElement]
    };

    // 如果某个五行过旺（数量>=4），则改用相克法
    for (const [element, count] of Object.entries(counts)) {
      if (count >= 4) {
        recommendation.method = 'restricts';
        recommendation.recommended = FiveElements.restricts[element];
        break;
      }
    }

    return {
      counts,
      recommendation,
      analysis: `根据您的八字，${minElement}行偏弱，建议佩戴${recommendation.recommended}行的水晶来调和五行。`
    };
  }

  // 格式化运势分析结果
  function formatAnalysis(text) {
    console.log('========== 开始格式化分析结果 ==========');
    console.log('输入类型:', typeof text);
    if (text) {
      console.log('输入长度:', text.length);
      console.log('输入前100字符:', text.substring(0, 100));
    } else {
      console.log('输入为空');
    }
    
    // 处理空文本或未定义
    if (!text) {
      console.log('分析结果为空，返回空数组');
      return [];
    }
    
    // 检查是否为Storage存储结果的URL链接
    if (typeof text === 'object' && text.resultStorageRef && text.resultStorageRef.url) {
      console.log('检测到Storage结果链接:', text.resultStorageRef.url);
      // 如果是从Storage加载的结果，使用摘要加链接
      return [
        text.resultSummary || "查看完整运势分析结果",
        `点击查看完整分析结果: [完整结果](${text.resultStorageRef.url})`
      ];
    }
    
    let formattedSections = [];
    
    // 尝试按照不同模式匹配内容
    
    // 检查是否包含标准节标题
    const hasStandardSections = text.includes("==== 2019-2024运势回顾 ====") || 
                               text.includes("==== 2025年运势预测 ====") || 
                               text.includes("==== 针对性建议 ====");
    
    console.log('是否包含标准节标题:', hasStandardSections);
    
    if (hasStandardSections) {
      console.log('使用标准节格式分割');
      
      // 捕获所有的标准节段落
      const sectionRegex = /====\s+([^=]+)\s+====\s*([\s\S]*?)(?=====\s+[^=]+\s+====|$)/g;
      let match;
      
      while ((match = sectionRegex.exec(text)) !== null) {
        const sectionTitle = match[1].trim();
        const sectionContent = match[2].trim();
        console.log(`找到节: ${sectionTitle}, 内容长度: ${sectionContent.length}`);
        formattedSections.push(`${sectionTitle}:\n${sectionContent}`);
      }
      
      // 如果没有匹配到任何节，尝试传统的分割方法
      if (formattedSections.length === 0) {
        console.log('正则表达式匹配失败，尝试使用split方法');
        // 使用标准节格式分割
        const sections = text.split(/====\s+([^=]+)\s+====/);
        console.log('split后的段落数:', sections.length);
        
        // 第一项通常是空的（分割前的内容）
        for (let i = 1; i < sections.length; i += 2) {
          if (i+1 < sections.length) {
            // 组合标题和内容
            const sectionTitle = sections[i].trim();
            const sectionContent = sections[i+1].trim();
            console.log(`找到节(split方法): ${sectionTitle}, 内容长度: ${sectionContent.length}`);
            formattedSections.push(`${sectionTitle}:\n${sectionContent}`);
          }
        }
      }
    } else {
      // 备选策略：提取标题部分
      if (text.startsWith('运势分析')) {
        formattedSections.push('运势分析。');
      }
      
      // 提取八字分析回顾部分标题
      const reviewTitle = text.match(/\*\*八字分析回顾[^*]+\*\*/);
      if (reviewTitle) {
        formattedSections.push(reviewTitle[0] + '。');
      }
      
      // 按年份提取分析内容
      const yearPattern = /\d+\.\s*\*\*\d{4}年[^0-9]+?(?=\d+\.\s*\*\*\d{4}年|\*\*2025年|\*\*针对性|$)/g;
      const yearMatches = text.match(yearPattern);
      if (yearMatches) {
        yearMatches.forEach(yearContent => {
          // 去除序号，保留年份标记和内容
          const cleanedContent = yearContent.replace(/^\d+\.\s*/, '');
          formattedSections.push(cleanedContent.trim());
        });
      }
      
      // 提取2025年预测部分
      const prediction2025 = text.match(/\*\*2025年预测[^*]+?(?=\*\*针对性|$)/);
      if (prediction2025) {
        formattedSections.push(prediction2025[0].trim());
      }
      
      // 提取建议部分
      const recommendations = text.match(/\*\*针对性建议[^*]+$/);
      if (recommendations) {
        formattedSections.push(recommendations[0].trim());
      }
    }
    
    // 如果没有匹配到任何部分但有文本，使用原始文本分段
    if (formattedSections.length === 0 && text.length > 0) {
      console.log('未能通过模式匹配格式化内容，尝试按段落分割');
      // 按段落分割文本
      formattedSections = text.split(/\n\n+/).filter(section => section.trim().length > 0);
      
      // 如果仍无法分割，直接显示完整文本
      if (formattedSections.length === 0) {
        console.log('无法分段，使用完整文本');
        formattedSections = [text];
      }
    }
    
    // 清理和标准化所有段落
    const finalSections = formattedSections
      .map(section => section.trim())
      .filter(section => section.length > 0)
      .map(section => section.endsWith('。') ? section : section + '。');
    
    console.log('格式化后的段落数量:', finalSections.length);
    if (finalSections.length > 0) {
      console.log('第一段:', finalSections[0]);
      if (finalSections.length > 1) {
        console.log('最后一段:', finalSections[finalSections.length - 1]);
      }
    } else {
      console.log('警告: 格式化后没有段落!');
    }
    console.log('========== 格式化分析结果完成 ==========');
    
    return finalSections;
  }

  // 获取分类数据
  const fetchCategories = async () => {
    try {
      // 从Firestore获取分类数据
      const categoriesRef = collection(db, 'categories');
      const querySnapshot = await getDocs(categoriesRef);
      const categoriesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (categoriesData.length === 0) {
        console.warn('未找到任何分类数据');
        // 显示空状态而不是使用模拟数据
        setError('未能获取分类数据，请联系客服');
      }
      
      setCategories(categoriesData);
    } catch (error) {
      console.error('获取分类失败:', error);
      setError('获取分类数据失败，请稍后再试');
      // 不使用默认数据，保持空数组
      setCategories([]);
    }
  };

  // 获取推荐商品
  const fetchProductsByCategoryId = async (categoryId) => {
    try {
      setLoadingProducts(true);
      // 从Firestore获取产品数据
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where("categoryId", "==", categoryId), where("status", "==", "active"));
      const querySnapshot = await getDocs(q);
      const productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // 确保图片URL格式正确
        images: doc.data().images?.map(img => 
          img.startsWith('http') ? img : img
        )
      }));
      
      if (productsData.length === 0) {
        console.log('未找到该分类的产品');
        // 不使用默认产品，UI将显示空状态
      }
      
      setRecommendedProducts(productsData);
    } catch (error) {
      console.error('获取推荐商品失败:', error);
      setError('获取推荐商品失败，请稍后再试');
      setRecommendedProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // 提交算命请求
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('====== 点击开始分析按钮 ======');
    
    // 验证用户是否登录
    if (!currentUser) {
      setError('该功能仅限登录用户使用，请先登录');
      return;
    }
    
    // 验证表单
    if (!form.birthDate || !form.birthShichen || !form.name || !form.gender) {
      setError('请填写完整信息 Please fill in all fields');
      return;
    }
    
    // 在提交前检查用户是否已达到限制
    try {
      const userId = currentUser.uid;
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // 获取计数相关数据
        const lastHourlyReset = userData.lastHourlyReset?.toDate() || new Date(0);
        const now = new Date();
        const hourlyTimeDiff = now - lastHourlyReset;
        const hourlyClickCount = userData.hourlyClickCount || 0;
        
        console.log('上次小时重置时间:', lastHourlyReset);
        console.log('当前时间:', now);
        console.log('距离上次重置的时间(毫秒):', hourlyTimeDiff);
        console.log('当前小时内点击次数:', hourlyClickCount);
        
        // 如果距离上次重置已超过1小时，重置计数器
        let currentHourlyCount;
        if (hourlyTimeDiff > 60 * 60 * 1000) {
          console.log('已超过1小时，重置计数器');
          // 稍后会在继续处理前更新Firestore
          currentHourlyCount = 0;
        } else {
          currentHourlyCount = hourlyClickCount;
        }
        
        console.log('当前有效小时点击次数:', currentHourlyCount);
        
        // 检查是否已达到每小时10次的限制
        if (currentHourlyCount >= 10) {
          // 显示错误消息
          const errorMessage = '您已超过1小时内使用10次AI算命的限制，如果需要更详细的命理分析请联系我们的命理师做进一步咨询';
          setError(errorMessage);
          
          // 弹出确认对话框并跳转到命理师页面
          setTimeout(() => {
            const confirmText = '您已超过1小时内使用10次AI算命的限制，如果需要更详细的命理分析请联系我们的命理师做进一步咨询。点击确认前往命理师页面。';
            if (window.confirm(confirmText)) {
              // 用户点击确认，跳转到fortune-teller页面
              window.location.href = '/fortune-teller';
            }
          }, 300); // 稍微减少延迟，使体验更流畅
          
          return; // 阻止继续执行分析
        }
        
        // 无论是否继续执行API调用，都递增计数器并更新时间戳
        try {
          // 递增计数器
          const newHourlyCount = currentHourlyCount + 1;
          console.log('递增后的点击次数:', newHourlyCount);
          
          // 如果需要重置，更新重置时间
          if (hourlyTimeDiff > 60 * 60 * 1000) {
            // 重置并设置新的时间戳
            await updateDoc(userRef, {
              hourlyClickCount: 1, // 第一次点击
              lastHourlyReset: now
            });
            console.log('重置计数器并更新时间戳');
          } else {
            // 仅递增计数器
            await updateDoc(userRef, {
              hourlyClickCount: newHourlyCount
            });
            console.log('递增计数器');
          }
        } catch (countError) {
          console.error('更新点击计数器失败:', countError);
          // 继续执行，即使计数器更新失败
        }
      }
    } catch (error) {
      console.error('检查用户限制失败:', error);
      // 继续执行，不阻止用户尝试
    }

    setLoading(true);
    setError('');
    setLoadingMessage("正在初始化命理分析...");
    setLoadingProgress(5);
    
    // 设置渐进式加载状态更新
    const loadingTimer = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 95) {
          clearInterval(loadingTimer);
          return 95;
        }
        return prev + 1;
      });
      
      // 更新加载消息
      if (loadingProgress < 20) {
        setLoadingMessage("正在连接AI命理师，这可能需要10-20秒...");
      } else if (loadingProgress < 50) {
        setLoadingMessage("AI命理师正在分析您的八字，请稍候...");
      } else if (loadingProgress < 80) {
        setLoadingMessage("正在生成详细命理分析，即将完成...");
      } else {
        setLoadingMessage("完成分析，正在整理结果...");
      }
    }, 750); // 每750毫秒更新一次
    
    try {
      const timeString = shichenToHourMap[form.birthShichen] || '00:00';
      
      // 计算八字
      const bazi = calculateBaZi(form.birthDate, timeString);
      
      // 分析五行
      const fiveElementAnalysis = analyzeFiveElements(bazi);
      
      console.log("本地八字和五行分析完成，准备调用API");
      
      let fortuneResult;
      let dominantElement;
      
      try {
        console.log('开始调用AI算命API，八字信息:', bazi);
        
        // 使用Firebase Callable函数 - 使用更长超时时间的函数实例
        const analyzeBaziFn = httpsCallable(functionsWithLongTimeout, 'analyzeBazi');
        
        // 添加网络错误监测
        let networkStatus = 'online';
        const checkNetworkStatus = () => {
          return navigator.onLine ? 'online' : 'offline';
        };
        
        // 检查网络状态
        networkStatus = checkNetworkStatus();
        if (networkStatus === 'offline') {
          console.warn('网络连接已断开，可能会影响算命功能...');
          setLoadingMessage("网络连接已断开，重试中...");
        }
        
        console.log('正在调用AI算命API...');
        setLoadingMessage("正在向AI命理师发送请求...");
        
        // 调用Firebase云函数
        const response = await analyzeBaziFn({
          bazi: bazi,
          name: form.name,
          gender: form.gender
        });
        
        console.log('API调用成功，获取响应:', response);
        
        // 从响应中获取结果
        if (response.data && response.data.success) {
          fortuneResult = response.data.data;
          console.log('成功获取算命结果');
        } else {
          throw new Error("API返回了错误的响应格式");
        }
        
        // 清除加载计时器
        clearInterval(loadingTimer);
        setLoadingProgress(100);
        setLoadingMessage("AI算命分析完成！");
        
        // 在控制台记录收到的完整算命结果
        console.log('========== AI生成的完整八字分析结果 ==========');
        console.log(typeof fortuneResult, '长度:', fortuneResult ? fortuneResult.length : 0);
        console.log(fortuneResult);
        console.log('========== 完整分析结果结束 ==========');
        
        // 分析算命结果，提取五行并推荐手串
        extractElementsAndRecommendBracelets(fortuneResult);
        
        // 提取主要五行元素
        dominantElement = fiveElementAnalysis.recommendation.recommended;
      } catch (apiError) {
        console.error('调用AI算命API失败:', apiError);
        console.error('错误详情:', apiError.code, apiError.message, apiError.details);
        
        // 清除加载计时器
        clearInterval(loadingTimer);
        setLoadingProgress(0);
        
        // 根据错误类型提供具体的错误信息
        let errorMessage = '算命分析请求失败，请稍后重试';
        
        // 常见错误类型处理
        if (apiError.code === 'functions/unavailable') {
          errorMessage = '服务暂时不可用，请稍后再试';
        } else if (apiError.code === 'functions/internal') {
          errorMessage = 'AI分析引擎暂时不可用，正在维护中';
        } else if (apiError.code === 'functions/unauthenticated') {
          errorMessage = '请先登录后再尝试使用此功能';
        } else if (apiError.code === 'functions/resource-exhausted') {
          errorMessage = '您已超过1小时内使用10次AI算命的限制，如果需要更详细的命理分析请联系我们的命理师做进一步咨询';
          // 添加确认后跳转逻辑
          setTimeout(() => {
            const confirmText = '您已超过1小时内使用10次AI算命的限制，如果需要更详细的命理分析请联系我们的命理师做进一步咨询。点击确认前往命理师页面。';
            if (window.confirm(confirmText)) {
              window.location.href = '/fortune-teller';
            }
          }, 300);
        } else if (apiError.message && apiError.message.includes('CORS')) {
          errorMessage = '网络连接受限，请刷新页面后再试';
        }
        
        setError(errorMessage);
        
        // 降级处理：使用本地计算提供基础服务
        console.log('API调用失败，使用本地降级功能...');
        
        // 使用已有的五行分析结果生成基本解读
        const offlineResult = generateOfflineResult(bazi, form.name, fiveElementAnalysis);
        
        // 设置结果
        setResult({
          bazi,
          fiveElements: fiveElementAnalysis,
          aspectAnalysis: offlineResult,
          isOfflineMode: true // 标记为离线模式结果
        });
        
        // 尝试在本地存储以便下次使用
        try {
          localStorage.setItem('lastBaziResult', JSON.stringify({
            bazi,
            fiveElements: fiveElementAnalysis,
            aspectAnalysis: offlineResult,
            isOfflineMode: true,
            timestamp: new Date().toISOString()
          }));
        } catch (e) {
          console.warn('无法保存到本地存储:', e);
        }
        
        // 获取推荐商品
        const recommendedElement = fiveElementAnalysis.recommendation.recommended;
        fetchRecommendedBracelets(recommendedElement);
        
        setLoading(false);
        return;
      }
      
      // 离线模式生成基础解读结果
      function generateOfflineResult(bazi, name, fiveElementAnalysis) {
        const counts = fiveElementAnalysis.counts;
        const minElement = fiveElementAnalysis.recommendation.element;
        const recommendedElement = fiveElementAnalysis.recommendation.recommended;
        const strongestElement = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
        
        return `**八字基本分析**
        
您的八字为：${bazi.year} ${bazi.month} ${bazi.day} ${bazi.hour}

根据五行分析，您的命盘中${strongestElement}行较强，而${minElement}行偏弱。${
          strongestElement === '木' ? '木行强表示您创造力强，具有进取精神，但可能有时过于固执。' :
          strongestElement === '火' ? '火行强表示您热情开朗，人缘好，但可能情绪波动较大。' :
          strongestElement === '土' ? '土行强表示您踏实稳重，责任感强，但可能缺乏变通。' :
          strongestElement === '金' ? '金行强表示您做事有条理，注重实际，但可能有些固执。' :
          '水行强表示您思维敏捷，适应能力强，但可能缺乏坚持。'
        }

**五行调和建议**

为平衡五行能量，建议佩戴${recommendedElement}行水晶，例如${
          recommendedElement === '木' ? '绿幽灵、翡翠或绿松石' :
          recommendedElement === '火' ? '红玛瑙、石榴石或红碧玺' :
          recommendedElement === '土' ? '黄水晶、虎睛石或黄玉' :
          recommendedElement === '金' ? '白水晶、月光石或钛晶' :
          '黑曜石、蓝宝石或紫水晶'
        }。这些水晶能帮助您增强${minElement}行能量，使五行更趋平衡。

**2024-2025年运势简析**

目前受网络限制，无法提供详细的运势分析。请在网络恢复后重新获取完整算命结果。

DestinyCrystal将为您推荐最适合的水晶饰品，帮助您改善运势。`;
      }
      
      // 保存到数据库 - 使用直接Firestore操作而不是Cloud Function
      try {
        console.log('开始保存算命结果到Firestore...');
        
        // 获取当前用户ID
        const userId = currentUser?.uid;
        if (!userId) {
          throw new Error('用户未登录或用户ID无效');
        }
        
        // 格式化八字为字符串格式
        const baziString = `${bazi.year} ${bazi.month} ${bazi.day} ${bazi.hour}`;
        console.log('格式化的八字:', baziString);
        
        // 生成唯一的八字标识符
        const baziIdentifier = `${bazi.year}-${bazi.month}-${bazi.day}-${bazi.hour}-${form.gender || 'male'}`;
        console.log('生成的八字标识符:', baziIdentifier);
        
        // 准备存储数据
        const timestamp = new Date();
        let resultData = null;
        let resultStorageRef = null;
        
        // 检查结果长度是否超过Firestore限制
        const isLongResult = fortuneResult && fortuneResult.length > 10000;
        
        if (isLongResult) {
          console.log(`结果长度(${fortuneResult.length}字符)超过10000，考虑替代存储方案`);
          
          // 对于超长结果，目前先直接在Firestore中存储摘要
          // 注意：在生产环境中，应该实现Storage上传逻辑或拆分文档
          resultData = fortuneResult.substring(0, 10000) + '...（结果已截断）';
          
          // 保存完整结果到本地存储
          try {
            localStorage.setItem('lastCompleteBaziResult', fortuneResult);
            console.log('已保存完整算命结果到本地存储');
          } catch (localError) {
            console.warn('保存到本地存储失败:', localError);
          }
        } else {
          // 直接存储完整结果
          resultData = fortuneResult;
        }
        
        // 创建Firestore文档数据
        const fortuneReadingData = {
          userId,
          name: form.name || '',
          bazi: baziString,
          input: JSON.parse(JSON.stringify(bazi)), // 深拷贝并移除undefined值
          weakElement: minElement || '',
          fiveElements: result.fiveElements.counts || {},
          createdAt: timestamp,
          result: resultData,
          gender: form.gender, // 保存性别信息
          element: dominantElement, // 保存推荐元素
          baziIdentifier, // 添加八字标识符 
          isLatestForBazi: true, // 标记为该八字的最新分析
          timestamp: timestamp.getTime() // 添加时间戳，确保排序正确
        };
        
        // 过滤掉对象中的所有undefined值
        const filterUndefined = (obj) => {
          const newObj = {...obj};
          Object.keys(newObj).forEach(key => {
            if (newObj[key] === undefined) {
              delete newObj[key];
            } else if (typeof newObj[key] === 'object' && newObj[key] !== null) {
              newObj[key] = filterUndefined(newObj[key]);
            }
          });
          return newObj;
        };
        
        // 过滤并保存到Firestore
        const cleanData = filterUndefined(fortuneReadingData);
        console.log('数据准备完成，字段:', Object.keys(cleanData).join(', '));
        
        // 获取Firestore集合引用
        const fortuneReadingsRef = collection(db, 'fortuneReadings');
        
        // 创建新的文档引用
        const newFortuneDoc = doc(fortuneReadingsRef);
        
        // 保存数据到Firestore
        await setDoc(newFortuneDoc, cleanData);
        console.log('算命结果保存成功，文档ID:', newFortuneDoc.id);
        
        // 更新用户的分析次数记录
        try {
          // 获取用户文档引用
          const userRef = doc(db, 'users', userId);
          
          // 获取当前用户数据
          const userDoc = await getDoc(userRef);
          const userData = userDoc.exists() ? userDoc.data() : {};
          
          // 检查是否超过使用限制 - 每小时10次
          const lastAnalysisTime = userData.lastBaziAnalysisTime?.toDate() || new Date(0);
          const now = timestamp;
          const timeDiff = now - lastAnalysisTime;
          const currentCount = userData.baziAnalysisCount || 0;
          
          // 如果时间差超过1小时，重置计数后+1，否则递增
          const newCount = (timeDiff > 60 * 60 * 1000) ? 1 : currentCount + 1;
          
          // 记录计数情况
          console.log('时间差(毫秒):', timeDiff);
          console.log('原始计数:', currentCount);
          console.log('新计数:', newCount);
          
          // 检查是否达到限制 - 这里不需要拦截，因为前面的检查已经拦截了
          if (newCount >= 10) {
            console.warn('用户分析次数即将超限');
            // 不抛出错误，因为已经完成了这次分析，但会记录警告
            // 下次分析会被限制
          }
          
          // 更新用户数据
          await updateDoc(userRef, {
            lastBaziAnalysisTime: timestamp,
            baziAnalysisCount: newCount
          });
          
          console.log('用户分析记录更新成功');
        } catch (userUpdateError) {
          console.error('更新用户记录失败:', userUpdateError);
          // 继续执行，即使用户记录更新失败
        }
      } catch (saveError) {
        console.error('保存算命结果到Firestore失败:', saveError);
        // 继续流程，不影响用户体验
        
        // 即使保存失败，也尝试在本地存储结果以便展示
        try {
          localStorage.setItem('lastBaziResult', JSON.stringify({
            bazi,
            result: fortuneResult,
            element: dominantElement,
            gender: form.gender, // 保存性别信息
            timestamp: new Date().toISOString()
          }));
          console.log('已保存算命结果到本地');
        } catch (localSaveError) {
          console.warn('本地保存失败:', localSaveError);
        }
      }
      
      // 获取对应的商品分类
      const recommendedElement = fiveElementAnalysis.recommendation.recommended;
      const matchedCategory = categories.find(cat => 
        cat.name === recommendedElement
      );
      
      // 记录结果对象的内容
      const resultObject = {
        bazi,
        fiveElements: fiveElementAnalysis,
        aspectAnalysis: fortuneResult,
        recommendedCategory: matchedCategory
      };
      
      console.log('========== 最终结果对象 ==========');
      console.log('包含字段:', Object.keys(resultObject).join(', '));
      console.log('八字:', resultObject.bazi);
      console.log('五行分析:', resultObject.fiveElements);
      console.log('aspectAnalysis类型:', typeof resultObject.aspectAnalysis);
      console.log('推荐分类:', resultObject.recommendedCategory);
      console.log('========== 最终结果对象结束 ==========');
      
      // 更新结果
      setResult(resultObject);

      // 获取推荐分类的产品
      if (matchedCategory) {
        await fetchProductsByCategoryId(matchedCategory.id);
      }
    } catch (error) {
      console.error('分析出错:', error);
      clearInterval(loadingTimer);
      setLoadingProgress(0);
      setError('分析过程出现错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 页面初始化获取分类数据
  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* SEO优化 */}
      <SeoHead
        title="AI智能算命 | 生辰八字分析 | 免费运势预测 | DestinyCrystal"
        description="基于先进AI技术的智能算命系统，只需输入生辰八字，即可获得精准的运势分析和人生指引，解答您的情感、事业、财运等方面困惑。"
        canonicalUrl="https://destiny-crystal.com/ai-fortune"
        keywords={["AI算命", "八字分析", "运势预测", "生辰八字", "爱情运势", "事业财运", "智能算命"]}
        schemaData={aiFortuneSchemaData}
      />
      <Typography variant="h4" gutterBottom align="center">
        AI算命 / AI Fortune Telling
      </Typography>
      
      <Grid container spacing={3}>
        {/* 表单部分 */}
        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              个人信息 / Personal Information
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="出生日期 Birth Date"
                    type="date"
                    name="birthDate"
                    value={form.birthDate}
                    onChange={handleFormChange}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel id="birth-shichen-label">出生时辰 Birth Time</InputLabel>
                    <Select
                      labelId="birth-shichen-label"
                      id="birthShichen"
                      name="birthShichen"
                      value={form.birthShichen}
                      onChange={handleFormChange}
                      label="出生时辰 Birth Time"
                      MenuProps={{
                        disablePortal: false,
                        disableScrollLock: false
                      }}
                    >
                      {shichenList.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="姓名 Name"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel id="gender-label">性别 Gender</InputLabel>
                    <Select
                      labelId="gender-label"
                      id="gender-select"
                      name="gender"
                      value={form.gender}
                      onChange={handleFormChange}
                      label="性别 Gender"
                      MenuProps={{
                        disablePortal: false,
                        disableScrollLock: false
                      }}
                    >
                      <MenuItem value="male">男 Male</MenuItem>
                      <MenuItem value="female">女 Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        {loadingMessage || "分析中..."}
                      </>
                    ) : '开始分析'}
                  </Button>
                  
                  {/* 加载进度条 */}
                  {loading && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress variant="determinate" value={loadingProgress} />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                          <Typography variant="body2" color="text.secondary">{`${Math.round(loadingProgress)}%`}</Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1, display: 'block' }}>
                        {loadingProgress < 95 ? 
                          "AI分析需要一段时间，请耐心等待..." : 
                          "即将完成，请稍候..."}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </form>
          </StyledPaper>
        </Grid>
        
        {/* 时辰对照表 */}
        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              时辰与24小时对照
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>时辰</TableCell>
                    <TableCell>对应时间</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shichenList.map((item) => (
                    <TableRow key={item.value}>
                      <TableCell>{item.value}</TableCell>
                      <TableCell>{item.label.split('(')[1].slice(0, -1)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledPaper>
        </Grid>
        
        {/* 结果展示区域 */}
        {result && (
          <Grid item xs={12}>
            <StyledPaper>
              <Typography variant="h5" gutterBottom>
                运势分析结果
              </Typography>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  尊敬的 {form.name}，以下是您的八字与运势分析：
                </Typography>
                
                {/* 八字排盘 */}
                <Box sx={{ mt: 4, mb: 4 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    八字排盘
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={3}>
                      <BaziColumn>
                        <Typography variant="subtitle1" gutterBottom color="primary">
                          年柱
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <BaziChar>{result.bazi.year[0]}</BaziChar>
                          <BaziChar>{result.bazi.year[1]}</BaziChar>
                        </Box>
                      </BaziColumn>
                    </Grid>
                    <Grid item xs={3}>
                      <BaziColumn>
                        <Typography variant="subtitle1" gutterBottom color="primary">
                          月柱
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <BaziChar>{result.bazi.month[0]}</BaziChar>
                          <BaziChar>{result.bazi.month[1]}</BaziChar>
                        </Box>
                      </BaziColumn>
                    </Grid>
                    <Grid item xs={3}>
                      <BaziColumn>
                        <Typography variant="subtitle1" gutterBottom color="primary">
                          日柱
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <BaziChar>{result.bazi.day[0]}</BaziChar>
                          <BaziChar>{result.bazi.day[1]}</BaziChar>
                        </Box>
                      </BaziColumn>
                    </Grid>
                    <Grid item xs={3}>
                      <BaziColumn>
                        <Typography variant="subtitle1" gutterBottom color="primary">
                          时柱
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <BaziChar>{result.bazi.hour[0]}</BaziChar>
                          <BaziChar>{result.bazi.hour[1]}</BaziChar>
                        </Box>
                      </BaziColumn>
                    </Grid>
                  </Grid>
                </Box>
                
                {/* 五行分析 */}
                <Box sx={{ mt: 4, mb: 4 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    五行分析
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {result.fiveElements.analysis}
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    {Object.entries(result.fiveElements.counts).map(([element, count]) => (
                      <Box key={element} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" sx={{ width: 40, fontWeight: 'medium' }}>
                          {element}
                        </Typography>
                        <ElementBar sx={{ width: count * 25 + 'px' }}>
                          {count}
                        </ElementBar>
                      </Box>
                    ))}
                  </Box>
                </Box>
                
                {/* 运势解读 */}
                {result.aspectAnalysis && (
                  <Box sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      运势分析
                    </Typography>
                    <Box sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                      {/* 处理格式化的分析结果 */}
                      {(() => {
                        try {
                          console.log('开始处理分析结果展示...');
                          // 如果结果是对象而不是字符串，可能是格式错误
                          if (typeof result.aspectAnalysis === 'object' && result.aspectAnalysis !== null) {
                            console.log('分析结果是对象，而不是字符串:', result.aspectAnalysis);
                            // 尝试将对象转换为JSON字符串
                            return (
                              <Typography variant="body1">
                                {JSON.stringify(result.aspectAnalysis, null, 2)}
                              </Typography>
                            );
                          }
                          
                          // 打印原始分析内容
                          console.log('========== 开始渲染分析内容 ==========');
                          console.log('原始分析结果类型:', typeof result.aspectAnalysis);
                          if (result.aspectAnalysis) {
                            console.log('原始分析结果长度:', result.aspectAnalysis.length);
                            console.log('原始分析结果前300字符:\n', result.aspectAnalysis.substring(0, 300));
                          } else {
                            console.log('原始分析结果为空!');
                            return <Typography variant="body1">无法获取分析结果，请重试</Typography>;
                          }
                          
                          // 尝试直接使用更简单的显示逻辑：按照==== 划分的段落显示
                          if (result.aspectAnalysis.includes('====')) {
                            // 用正则表达式匹配"==== 标题 ===="格式的标题
                            const sections = result.aspectAnalysis.split(/====\s+([^=]+)\s+====/);
                            const formattedSections = [];
                            
                            // 第一个元素通常是空的，从第二个开始处理
                            for (let i = 1; i < sections.length; i += 2) {
                              const title = sections[i].trim();
                              const content = (i+1 < sections.length) ? sections[i+1].trim() : '';
                              
                              if (title && content) {
                                formattedSections.push({ title, content });
                              }
                            }
                            
                            console.log(`找到${formattedSections.length}个标准格式段落`);
                            
                            // 如果找到标准格式段落，显示它们
                            if (formattedSections.length > 0) {
                              return (
                                <>
                                  {formattedSections.map(({ title, content }, index) => (
                                    <Box key={index} sx={{ mb: 4 }}>
                                      <Typography variant="h6" gutterBottom color="primary">
                                        {title}
                                      </Typography>
                                      {content.split('\n').map((paragraph, pIndex) => (
                                        paragraph.trim() && (
                                          <Typography key={pIndex} variant="body1" paragraph>
                                            {paragraph}
                                          </Typography>
                                        )
                                      ))}
                                    </Box>
                                  ))}
                                </>
                              );
                            }
                          }
                          
                          // 后备方案：直接按行分割显示
                          console.log('使用后备显示方式 - 直接按行分割');
                          const paragraphs = result.aspectAnalysis
                            .split('\n')
                            .filter(line => line.trim().length > 0);
                          
                          console.log(`分割为${paragraphs.length}个段落`);
                          
                          return (
                            <>
                              {paragraphs.map((paragraph, index) => (
                                <Typography 
                                  key={index} 
                                  variant="body1" 
                                  paragraph
                                  sx={{ 
                                    fontWeight: paragraph.includes('【') ? 'bold' : 'normal',
                                    color: paragraph.includes('====') ? 'primary.main' : 'text.primary'
                                  }}
                                >
                                  {paragraph}
                                </Typography>
                              ))}
                            </>
                          );
                        } catch (error) {
                          console.error('显示分析结果时出错:', error);
                          // 发生错误时，显示原始内容，确保用户能看到结果
                          return (
                            <Typography variant="body1">
                              {typeof result.aspectAnalysis === 'string' 
                                ? result.aspectAnalysis 
                                : '分析结果格式错误，请联系客服。'}
                            </Typography>
                          );
                        }
                      })()}
                    </Box>
                  </Box>
                )}
                
                {/* 推荐商品 */}
                {result && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      推荐水晶
                    </Typography>
                  
                  {loadingProducts ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body1" sx={{ mt: 2 }}>
                        正在为您匹配最适合的水晶产品...
                      </Typography>
                    </Box>
                  ) : recommendedProducts.length > 0 ? (
                    <>
                      <Typography variant="body1" paragraph>
                        根据您的八字五行分析，我们为您精选了以下适合您的水晶饰品：
                      </Typography>
                      <Grid container spacing={3}>
                        {recommendedProducts.map(product => (
                          <Grid item xs={12} sm={6} md={4} key={product.id}>
                            <ProductCard product={product} />
                          </Grid>
                        ))}
                      </Grid>
                      <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          这些水晶产品经专业命理师精心挑选，能有效调和您的五行能量，提升运势。
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          点击产品查看详情或加入购物车。
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      暂时没有找到匹配的产品，请稍后再试。
                    </Typography>
                  )}
                </Box>
                )}
              </Box>
            </StyledPaper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default AiFortune;